import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { AlertTriangle, CheckCircle2, X, Loader2, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

/**
 * Provider dashboard banner for active patient safety alerts (crisis-keyword detection).
 * Lists unacknowledged alerts; provider taps "Mark reviewed" to dismiss.
 *
 * The alert payload is HIPAA-minimized — we never display the patient's actual message,
 * just that something flagged + which surface (Messages / ADHD Assistant).
 */
export default function SafetyAlertsBanner() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/safety-alerts", { params: { only_unack: true, limit: 20 } });
      setAlerts(data?.alerts || []);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Refresh when a new safety alert arrives via WS
    const handler = () => load();
    window.addEventListener("realtime:safety_alert", handler);
    return () => window.removeEventListener("realtime:safety_alert", handler);
  }, [load]);

  const ack = async (id) => {
    setBusyId(id);
    try {
      await API.post(`/safety-alerts/${id}/acknowledge`);
      setAlerts(prev => prev.filter(a => a.id !== id));
      toast({ title: "Alert acknowledged" });
    } catch (err) {
      toast({ title: "Couldn't acknowledge", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyId(null); }
  };

  const falseAlarm = async (id) => {
    setBusyId(id);
    try {
      const { data } = await API.post(`/safety-alerts/${id}/false-alarm`);
      setAlerts(prev => prev.filter(a => a.id !== id));
      if (data?.auto_suppressed) {
        toast({
          title: "Phrase suppressed",
          description: `"${data.phrase}" has been auto-suppressed after 3 false alarms — it'll no longer trigger alerts. Manage in Provider settings.`,
          duration: 5000,
        });
      } else {
        toast({
          title: "Marked as false alarm",
          description: `Counted ${data?.false_alarm_count_for_phrase || 1} false alarm(s) for "${data?.phrase || "this phrase"}".`,
        });
      }
    } catch (err) {
      toast({ title: "Couldn't mark as false alarm", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyId(null); }
  };

  if (loading) return null;
  if (alerts.length === 0) return null;

  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-4 space-y-3" data-testid="safety-alerts-banner" role="alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-red-600" />
        <h3 className="font-semibold text-red-900 text-sm">
          {alerts.length === 1 ? "1 patient flagged for safety follow-up" : `${alerts.length} patients flagged for safety follow-up`}
        </h3>
      </div>
      <p className="text-xs text-red-800/80 leading-relaxed">
        Crisis indicators were detected in messages from these patients. Reach out within your practice's response window to confirm safety.
        The actual message content is not stored here — open the patient's thread to review.
      </p>
      <div className="space-y-2">
        {alerts.map(a => (
          <div
            key={a.id}
            className="bg-white border border-red-200 rounded-xl p-3 flex items-center justify-between gap-3"
            data-testid={`safety-alert-row-${a.id}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{a.patient_name || a.patient_email}</p>
              <p className="text-[11px] text-muted-foreground">
                <span className="capitalize">{(a.source || "").replace("_", " ")}</span>
                {" · "}
                {a.created_date ? formatDistanceToNow(new Date(a.created_date), { addSuffix: true }) : ""}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => ack(a.id)}
                disabled={busyId === a.id}
                className="rounded-full gap-1.5"
                data-testid={`safety-alert-ack-${a.id}`}
              >
                {busyId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Reviewed
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => falseAlarm(a.id)}
                disabled={busyId === a.id}
                className="rounded-full gap-1.5 text-muted-foreground hover:text-foreground"
                title={`Mark as false alarm — phrase: "${a.matched_phrase || "?"}"`}
                data-testid={`safety-alert-false-alarm-${a.id}`}
              >
                <ShieldOff className="w-3.5 h-3.5" />
                False alarm
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
